// src/UIManager.js
import { formatLabel } from "./Utils.js";
import { EKGRenderer } from "./EKGRenderer.js";
import { audio } from "./AudioManager.js";


/**
 * UIManager handles all DOM manipulations.
 * It follows a data-driven approach by listening to state changes.
 */
export class UIManager {
    constructor(elements, config) {
        this.ui = elements;
        this.config = config;
        
        // Surgical Optimization: Performance Bus
        this.activeAnimations = new Map(); // id -> { start, end, startTime, duration, el }
        this.animationFrameId = null;
        this.animateAll = this.animateAll.bind(this);

        // Throttling for high-frequency DOM updates
        this.lastStabilityUpdate = 0;
        this.stabilityThrottle = 150; // ms
        
        this._setupMobileTooltips();

        // EKG Real-time Animation
        const ekgCanvas = document.getElementById('ekg-canvas');
        if (ekgCanvas) {
            this.ekgRenderer = new EKGRenderer(ekgCanvas);
            this.ekgRenderer.resize();
            
            // Sync sound with visual peak
            this.ekgRenderer.onPeak = (isCritical) => {
                if (this.ui.gameArea && this.ui.gameArea.style.display === 'block') {
                    if (audio && audio.playHeartbeat) audio.playHeartbeat(isCritical);
                }
            };

            window.addEventListener('resize', () => this.ekgRenderer.resize());
            this._startEKGLoop();
        }

        if (window.lucide) {
            window.lucide.createIcons();
        }
    }

    _startEKGLoop() {
        const loop = (timestamp) => {
            if (this.ekgRenderer) {
                this.ekgRenderer.update(timestamp);
            }
            requestAnimationFrame(loop);
        };
        requestAnimationFrame(loop);
    }

    /**
     * Tactile Tooltips for Mobile: stat-boxes become actionable by click.
     */
    _setupMobileTooltips() {
        document.querySelectorAll('.stat-box').forEach(box => {
            box.addEventListener('click', (e) => {
                e.stopPropagation();
                const tooltipText = box.getAttribute('data-tooltip');
                if (tooltipText) this.notify(tooltipText, 'info');
            });
        });
    }

    /**
     * Main entry point for state-to-UI mapping.
     */
    update(state, levelState) {
        if (!state) return;

        // --- Animated Stats ---
        this._animateValue('credits', state.credits);
        this._animateValue('xp', state.playerXp);
        if (this.ui.levelValue) this.ui.levelValue.textContent = state.playerLevel || 1;
        if (this.ui.stamina) this.ui.stamina.textContent = `${state.stamina}/${state.maxStamina || 5}`;

        const btnLibrary = document.getElementById('runLibrary');
        if (btnLibrary) {
            // Surface real spendable talent points (skillPoints) instead of the dead studyPoints counter
            const sp = state.skillPoints || 0;
            const spBadge = sp > 0 ? ` <small style="opacity:0.8; font-size:0.75rem; margin-left:4px;">(${sp} SP)</small>` : '';
            btnLibrary.innerHTML = `<span>📚 MIS APUNTES</span>${spBadge}`;
        }

        // --- Guard Button Logic ---
        const btnGuardDirect = document.getElementById('runGuardDirect');
        if (btnGuardDirect) {
            if (state.stamina <= 0) {
                btnGuardDirect.disabled = true;
                btnGuardDirect.innerHTML = `🏥 SIN ENERGÍA`;
                btnGuardDirect.style.opacity = "0.5";
            } else {
                btnGuardDirect.disabled = false;
                btnGuardDirect.innerHTML = `🏥 INICIAR GUARDIA RÁPIDA`;
                btnGuardDirect.style.opacity = "1";
            }
        }

        const btnGuard = document.getElementById('runGuard');
        if (btnGuard) {
            if (state.stamina <= 0) {
                btnGuard.disabled = true;
                btnGuard.innerHTML = `🗺️ VER MAPA`;
                btnGuard.style.opacity = "0.5";
            } else {
                btnGuard.disabled = false;
                btnGuard.innerHTML = `🗺️ VER MAPA`;
                btnGuard.style.opacity = "1";
            }
        }

        // --- Level & XP ---
        if (levelState) {
            if (this.ui.levelValue) this.ui.levelValue.textContent = levelState.level;
            if (this.ui.levelBar) this.ui.levelBar.style.width = `${Math.min(100, levelState.progress * 100)}%`;
            if (this.ui.levelText) {
                this.ui.levelText.textContent = `${Math.round(levelState.xpIntoLevel)}/${Math.round(levelState.xpForNextLevel)} XP`;
            }
        }

        // --- RPG Stats ---
        if (this.ui.spCount) this.ui.spCount.innerText = state.rpgProfile ? state.rpgProfile.skillPoints : 0;
    }

    /**
     * Updates the learning-specific UI.
     */
    updateLearning(learningState) {
        if (this.ui.streak) this.ui.streak.textContent = learningState.streak;
        if (this.ui.bestStreak) this.ui.bestStreak.textContent = learningState.bestStreak;
    }

    /**
     * Updates the progress bar within a shift.
     */
    updateProgress(current, total, isChain = false) {
        if (this.ui.progressBar) {
            const percent = total === 0 ? 0 : (current / total) * 100;
            this.ui.progressBar.style.width = `${percent}%`;
        }
        if (this.ui.progressText) this.ui.progressText.textContent = `${current}/${total}`;
        this.updateShiftPhase(current, total, isChain);
    }

    /**
     * Updates the 3-step timeline.
     */
    updateShiftPhase(current, total, isChain = false) {
        for (let i = 0; i < 3; i++) {
            const step = document.getElementById(`tl-step-${i}`);
            if (!step) continue;
            step.classList.remove('active', 'completed');
            if (i < current) step.classList.add('completed');
            else if (i === current) step.classList.add('active');
        }

        const phaseTag = document.getElementById('case-phase-tag');
        if (phaseTag) {
            if (isChain) {
                const phases = ['FASE 1: ADMISIÓN', 'FASE 2: ABORDAJE', 'FASE 3: RESOLUCIÓN'];
                phaseTag.textContent = phases[current] || 'FASE FINAL';
            } else {
                phaseTag.textContent = `PACIENTE ${current + 1}`;
            }
        }
    }

    /**
     * Updates the Vitals HUD.
     */
    updateVitals(vitals, stability) {
        if (!vitals) return;
        
        const taEl = document.getElementById('vital-ta');
        const fcEl = document.getElementById('vital-fc');
        const satEl = document.getElementById('vital-sat');
        const mentalEl = document.getElementById('vital-mental');

        if (taEl) taEl.textContent = `${Math.round(vitals.ta_sys)}/${Math.round(vitals.ta_dia)}`;
        
        if (fcEl) {
            const fc = Math.round(vitals.fc);
            fcEl.textContent = fc;
            const isCritical = fc > 100 || fc < 60;
            fcEl.classList.toggle('critical', isCritical);
            
            if (this.ekgRenderer) {
                this.ekgRenderer.setBPM(fc, isCritical);
            }
        }
        
        if (satEl) {
            satEl.textContent = `${Math.round(vitals.satO2)}%`;
            const isHypoxic = vitals.satO2 < 92;
            satEl.classList.toggle('critical', isHypoxic);
        }
        
        if (mentalEl) mentalEl.textContent = vitals.mental;

        const now = Date.now();
        if (now - this.lastStabilityUpdate >= this.stabilityThrottle) {
            const stabilityText = document.getElementById('patient-stability');
            const stabilityBar = document.getElementById('stability-bar');

            if (stabilityText) stabilityText.textContent = `${Math.round(stability)}%`;
            if (stabilityBar) {
                stabilityBar.style.width = `${stability}%`;
                // Bauhaus CSS properties can handle colors via transitions or classes
            }
            this.lastStabilityUpdate = now;
        }
    }

    /**
     * Applies cognitive fatigue visual effects.
     */
    applyVisualBurnout(burnout) {
        const area = document.getElementById('encounter-area');
        if (!area) return;

        if (burnout > 70) {
            const intensity = (burnout - 70) / 30;
            area.style.filter = `sepia(${intensity * 0.4}) contrast(${1 + intensity * 0.2}) blur(${intensity * 0.5}px)`;
            if (burnout > 90) area.classList.add('danger-pulse');
            else area.classList.remove('danger-pulse');
        } else {
            area.style.filter = 'none';
            area.classList.remove('danger-pulse');
        }
    }

    switchTab(tabId) {
        // Use classList for consistency with CSS (.hub-tab-panel { display:none }, .active { display:block })
        this.ui.tabPanels.forEach(p => p.classList.remove('active'));
        this.ui.tabBtns.forEach(b => b.classList.remove('active'));

        const targetPanel = document.getElementById(tabId);
        const targetBtn = Array.from(this.ui.tabBtns).find(b => b.dataset.tab === tabId);

        if (targetPanel) targetPanel.classList.add('active');
        if (targetBtn) targetBtn.classList.add('active');
    }

    notify(text, type = 'success') {
        const container = document.getElementById('toast-container') || document.body;
        const toast = document.createElement('div');
        toast.className = `toast ${type} show`;
        toast.textContent = text;
        container.appendChild(toast);
        setTimeout(() => toast.remove(), 4000);
    }

    /**
     * Shows a contextual tooltip anchored to a DOM element.
     * Used primarily for onboarding and guidance.
     */
    showTooltip(targetSelector, text) {
        const target = document.querySelector(targetSelector);
        if (!target) return;

        // Clean up previous tooltips
        document.querySelectorAll('.clinical-tooltip').forEach(el => el.remove());

        const tooltip = document.createElement('div');
        tooltip.className = 'clinical-tooltip';
        tooltip.innerHTML = `
            <div class="tip-content">${text}</div>
            <button class="tip-close">&times;</button>
        `;

        document.body.appendChild(tooltip);

        const rect = target.getBoundingClientRect();
        const scrollX = window.scrollX || window.pageXOffset;
        const scrollY = window.scrollY || window.pageYOffset;

        // Positioning logic: try to center below target
        let left = rect.left + scrollX + (rect.width / 2) - 125;
        let top = rect.bottom + scrollY + 12;

        // Viewport constraints
        if (left < 10) left = 10;
        if (left + 260 > window.innerWidth) left = window.innerWidth - 270;

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;

        tooltip.querySelector('.tip-close').onclick = (e) => {
            e.stopPropagation();
            tooltip.remove();
        };

        // Auto-cleanup
        setTimeout(() => { if (tooltip.parentElement) tooltip.remove(); }, 12000);
    }


    showMentorInsight(insights) {
        const container = document.getElementById('encounter-area');
        if (!container) return;

        const bubble = document.createElement('div');
        bubble.className = 'hint-bubble card premium-glow';
        
        let content = Array.isArray(insights) ? insights.join('<br>') : insights;
        bubble.innerHTML = `
            <div class="hint-bubble-title">Insight del Mentor</div>
            <div class="hint-bubble-text">${content}</div>
            <button class="glass-btn hint-bubble-btn" onclick="this.parentElement.remove()">Entendido</button>
        `;
        container.appendChild(bubble);
        setTimeout(() => { if (bubble.parentElement) bubble.remove(); }, 10000);
    }

    /**
     * Triggers visual juice (shake for error, flash for impact)
     */
    showResultFeedback(type) {
        const station = document.querySelector('.medical-station');
        if (!station) return;

        if (type === 'correct') {
            station.classList.add('premium-glow');
            setTimeout(() => station.classList.remove('premium-glow'), 600);
        } else {
            station.classList.add('shake-extreme');
            setTimeout(() => station.classList.remove('shake-extreme'), 400);

            // Red Flash: Visual Feedback for Error
            const flash = document.createElement('div');
            flash.className = 'error-flash-overlay';
            document.body.appendChild(flash);
            setTimeout(() => flash.remove(), 300);
        }
    }

    achievement(title, desc) {
        const container = document.getElementById('toast-container') || document.body;
        const toast = document.createElement('div');
        toast.className = `toast achievement show`;
        toast.innerHTML = `
            <div style="font-size:0.7rem; text-transform:uppercase; opacity:0.8;">Logro Desbloqueado</div>
            <div style="font-weight:bold;">${title}</div>
            <div style="font-size:0.8rem;">${desc}</div>
        `;
        container.appendChild(toast);
        setTimeout(() => toast.remove(), 5000);
    }

    /**
     * Specialized notification for Dr. Swipe Clinical Pearl discovery.
     */
    showPearlDiscovery(pearl) {
        const container = document.getElementById('toast-container') || document.body;
        const toast = document.createElement('div');
        const rarityDisplay = String(pearl.rarity || 'Bronce');
        toast.className = `toast info show discovery-pearl rarity-${rarityDisplay.toLowerCase()} discovery-pearl-toast`;
        
        toast.innerHTML = `
            <div style="font-size:0.65rem; font-weight:900; text-transform:uppercase; color:var(--primary); margin-bottom:4px;">✨ NUEVA PERLA DESCUBIERTA</div>
            <div style="font-weight:800; font-size:1rem; line-height:1.1; margin-bottom:4px;">${formatLabel(pearl.topic)}: ${pearl.title || ''}</div>
            <div style="font-size:0.75rem; opacity:0.8; font-style:italic;">Se ha añadido al Atlas Clínico. +100 XP</div>
        `;

        container.appendChild(toast);
        setTimeout(() => toast.remove(), 6000);
    }

    renderRoster(roster, activeMentorId, secondaryMentorId, onEquip) {
        const container = this.ui.rosterContainer;
        if (!container) return;

        const mentorsDB = window.MENTORS_DB || [];
        const rosterKeys = Object.keys(roster || {});

        if (rosterKeys.length === 0) {
            container.innerHTML = '<p style="opacity:0.7; text-align:center; padding: 30px 20px;">No has desbloqueado mentores aún.<br><small>¡Haz tu primer pull para reclutar uno!</small></p>';
            return;
        }

        const fragment = document.createDocumentFragment();
        rosterKeys.forEach(id => {
            const entry = roster[id];
            const mentor = mentorsDB.find(m => m.id === id) || {
                name: id, rarity: entry.rarity || 'R', title: 'Residente'
            };
            const rarityColors = { SSR: '#f59e0b', SR: '#8b5cf6', R: '#3b82f6', C: '#94a3b8' };
            const color = rarityColors[mentor.rarity] || '#94a3b8';
            const icon = mentor.icon || '📁';
            
            const isPrimary = activeMentorId === id;
            const isSecondary = secondaryMentorId === id;

            const card = document.createElement('div');
            card.className = `card rarity-${mentor.rarity} roster-card ${isPrimary || isSecondary ? 'selected' : ''}`;
            card.style.setProperty('--card-border-color', isPrimary ? 'var(--primary)' : isSecondary ? 'var(--secondary)' : color);
            
            const affinityReq = (entry.level || 1) * 100;
            const affinityPercent = Math.min(100, (entry.affinity / affinityReq) * 100);

            card.innerHTML = `
              <div style="font-size: 2rem; margin-bottom: 8px; color: ${color};"><i data-lucide="${icon}"></i></div>
              <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px;">
                <div>
                  <div style="font-weight:bold; font-size:1.1rem; color:${color}; line-height:1.1;">${mentor.name}</div>
                  <div style="font-size:0.7rem; opacity:0.7; margin-top:4px; text-transform:uppercase; letter-spacing:0.05em;">${mentor.title || ''}</div>
                </div>
                <span style="font-size:0.65rem; font-weight:900; padding:3px 10px; border-radius:var(--radius-hard); background:${color}; color:#fff;">${mentor.rarity}</span>
              </div>
              
              ${isPrimary ? '<div style="font-size:0.6rem; color:var(--primary); font-weight:900; margin-bottom:5px;">⭐ EQUIPADO (PRINCIPAL)</div>' : ''}
              ${isSecondary ? '<div style="font-size:0.6rem; color:var(--secondary); font-weight:900; margin-bottom:5px;">🛡️ EQUIPADO (SECUNDARIO)</div>' : ''}

              <div style="margin-top:12px; font-size:0.7rem; display:flex; justify-content:space-between; opacity:0.8;">
                <span>Nivel ${entry.level || 1}</span>
                <span>${entry.affinity || 0}/${affinityReq}</span>
              </div>
              <div class="affinity-bar">
                <div class="affinity-fill" style="width: ${affinityPercent}%; background: ${color};"></div>
              </div>

              <div style="display:grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 15px;">
                <button class="glass-btn equip-p" style="font-size:0.65rem; padding: 5px;" data-id="${id}" data-slot="primary">Principal</button>
                <button class="glass-btn equip-s" style="font-size:0.65rem; padding: 5px;" data-id="${id}" data-slot="secondary">Secundario</button>
              </div>

              <div style="margin-top:10px; font-size:0.65rem; font-style:italic; opacity:0.5; border-top:1px solid rgba(0,0,0,0.05); padding-top:5px;">
                Info: Tap para detalle / Lore.
              </div>
            `;

            // Mobile: Lore-on-Tap [cite: 5, 12]
            card.onclick = (e) => {
                const isBtn = e.target.tagName === 'BUTTON';
                if (isBtn) {
                   const slot = e.target.dataset.slot;
                   if (onEquip) onEquip(id, slot);
                   return;
                }

                const modal = document.createElement('div');
                modal.className = 'modal-overlay';
                modal.style.zIndex = '9999';
                modal.innerHTML = `
                    <div class="modal-content card mentor-detail-modal-content">
                        <button class="close-btn">&times;</button>
                        <h3>${mentor.name}</h3>
                        <div class="mentor-meta">${mentor.title} (${mentor.rarity})</div>
                        <div class="mentor-lore">"${mentor.lore}"</div>
                        <div class="mentor-skill">
                            <strong style="color: var(--primary);">Habilidad Activa:</strong> <span style="font-weight: bold;">${mentor.active_skill_name}</span>
                            <div class="skill-desc">${mentor.active_skill_desc}</div>
                        </div>
                        <div class="mentor-passive">
                            <strong style="color: var(--primary);">Efecto Pasivo:</strong>
                            <div class="skill-desc">${mentor.passive_desc}</div>
                        </div>
                        <button class="glass-btn close-modal-btn">Cerrar</button>
                    </div>
                `;
                document.body.appendChild(modal);
                const closeModal = () => modal.remove();
                modal.querySelector('.close-btn').onclick = closeModal;
                modal.querySelector('.close-modal-btn').onclick = closeModal;
                modal.onclick = (e) => { if (e.target === modal) closeModal(); };
            };

            fragment.appendChild(card);
        });

        container.innerHTML = '';
        container.appendChild(fragment);
        if (window.lucide) window.lucide.createIcons();
    }

    renderAtlas(pearls, filter = 'all', modifiers = null) {
        const container = document.getElementById('atlas-list');
        if (!container) return;

        let entries = Object.entries(pearls);
        if (filter === 'forgotten') {
            const now = Date.now();
            entries = entries.filter(([id, data]) => now - data.seenAt > 48 * 3600 * 1000);
        }

        if (entries.length === 0) {
            container.innerHTML = `<p style="text-align:center; grid-column: 1/-1; opacity: 0.5;">No hay perlas para mostrar.</p>`;
            return;
        }

        const showTypicalError = modifiers?.ui?.atlasShowErrorTipico;

        const fragment = document.createDocumentFragment();
        entries.sort((a, b) => b[1].seenAt - a[1].seenAt).forEach(([id, data]) => {
            const card = document.createElement('div');
            const pearlRarity = String(data.rarity).toLowerCase();
            card.className = `atlas-card rarity-${pearlRarity}`;
            
            let innerHTML = `<div class="atlas-meta"><span class="atlas-rarity">${data.rarity}</span><span class="atlas-topic">${formatLabel(data.topic)}</span></div><div class="atlas-pearl">${data.pearl}</div>`;
            if (showTypicalError && typeof data.error_tipico === 'string' && data.error_tipico.trim()) {
                innerHTML += `<div class="atlas-error-tipico" style="font-size:0.75rem; opacity:0.8; margin-top:8px; border-top:1px dashed rgba(0,0,0,0.1); padding-top:6px; color:var(--accent);"><strong>Error Común:</strong> ${data.error_tipico}</div>`;
            }
            card.innerHTML = innerHTML;
            fragment.appendChild(card);
        });


        container.innerHTML = '';
        container.appendChild(fragment);
    }

    /**
     * Renders the local performance ranking and personal stats dashboard.
     * @param {Object} state - economy.exportState()
     */
    renderRanking(state) {
        // Works for both hub tab and standalone modal
        ['ranking-hub-list', 'ranking-list'].forEach(id => {
            const container = document.getElementById(id);
            if (!container) return;
            container.innerHTML = this._buildRankingHTML(state);
        });
    }

    _buildRankingHTML(state) {
        if (!state) {
            return `<p style="text-align:center; opacity:0.6; padding:20px;">Cargando estadísticas...</p>`;
        }

        const level = state.playerLevel || 1;
        const xp = state.playerXp || 0;
        const credits = state.credits || 0;
        const guards = state.dailyGuards || 0;
        
        // Extraer estadísticas de dominio de FSRS
        const domains = [];
        if (state.caseHistory) {
            for (const [key, val] of Object.entries(state.caseHistory)) {
                if (key.startsWith('__topic_')) {
                    const name = key.replace('__topic_', '');
                    const total = val.seen || 0;
                    if (total > 0) {
                        const acc = Math.round((val.correct || 0) / total * 100);
                        domains.push({ name, total, acc, stability: val.stability || 0 });
                    }
                }
            }
        }
        
        domains.sort((a, b) => b.acc - a.acc); // Ordenar por precisión

        let domainHTML = '';
        if (domains.length === 0) {
            domainHTML = `<div style="text-align:center; opacity:0.6; padding:20px; border:2px dashed var(--ink);">Juega guardias para ver tu dominio por especialidad.</div>`;
        } else {
            domainHTML = domains.map(d => {
                const color = d.acc >= 80 ? '#16a34a' : d.acc >= 60 ? '#d97706' : '#dc2626';
                const sColor = d.stability >= 50 ? '#3b82f6' : 'var(--ink)';
                return `
                <div style="display:flex; justify-content:space-between; align-items:center; padding:10px; border-bottom:1px solid rgba(0,0,0,0.06); background: rgba(0,0,0,0.02); margin-bottom:4px;">
                    <div>
                        <div style="font-weight:bold; font-size:0.9rem;">${d.name}</div>
                        <div style="font-size:0.7rem; opacity:0.7;">Vistos: ${d.total} | Retención FSRS: <span style="color:${sColor}">${Math.round(d.stability)}</span></div>
                    </div>
                    <div style="font-size:1.2rem; font-weight:900; color:${color};">${d.acc}%</div>
                </div>`;
            }).join('');
        }

        const statRow = (icon, label, value, sub = '') => `
            <div style="display:flex; justify-content:space-between; align-items:center; padding:8px 0; border-bottom:1px solid rgba(0,0,0,0.06);">
                <span style="font-size:0.85rem;">${icon} ${label}</span>
                <span style="font-weight:700; font-size:0.9rem;">${value}${sub ? `<small style="font-weight:400; opacity:0.6; margin-left:4px;">${sub}</small>` : ''}</span>
            </div>`;

        return `
            <div style="padding:4px 0;">
                <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:16px;">
                    <div style="border:2px solid var(--ink); padding:12px; text-align:center;">
                        <div style="font-size:0.6rem; text-transform:uppercase; opacity:0.6; margin-bottom:4px;">Nivel Actual</div>
                        <div style="font-size:1.6rem; font-weight:900; color:var(--primary);">Lv ${level}</div>
                        <div style="font-size:0.6rem; opacity:0.5;">${xp.toLocaleString()} XP total</div>
                    </div>
                    <div style="border:2px solid var(--ink); padding:12px; text-align:center;">
                        <div style="font-size:0.6rem; text-transform:uppercase; opacity:0.6; margin-bottom:4px;">Guardias Completadas</div>
                        <div style="font-size:1.6rem; font-weight:900; color:var(--ink);">${guards}</div>
                        <div style="font-size:0.6rem; opacity:0.5;">Día de Residencia</div>
                    </div>
                </div>

                <div style="margin-bottom:16px;">
                    <h3 style="font-size:0.85rem; text-transform:uppercase; opacity:0.8; margin-bottom:8px; border-bottom:2px solid var(--primary); padding-bottom:4px;">Rendimiento por Especialidad</h3>
                    ${domainHTML}
                </div>

                <div style="margin-bottom:16px;">
                    ${statRow('🪙', 'Créditos Acumulados', credits.toLocaleString())}
                </div>
            </div>
        `;
    }


    setLoading(btnId, isLoading) {
        const btn = document.getElementById(btnId);
        if (!btn) return;
        btn.disabled = isLoading;
        if (isLoading) {
            btn.dataset.prevText = btn.innerHTML;
            btn.innerHTML = `<span class="spinner">⏳</span> Procesando...`;
            btn.classList.add('loading');
        } else {
            btn.innerHTML = btn.dataset.prevText || btn.innerHTML;
            btn.classList.remove('loading');
        }
    }

    /**
     * High-Fidelity Gacha Animation: Reveal mentors recruited.
     * Supports skipping and grid summary.
     */
    showPullAnimation(results) {
        if (!results) return;
        const queue = Array.isArray(results) ? [...results] : [results];
        const overlay = document.getElementById('gacha-reveal');
        const card = document.getElementById('gacha-card');
        const rarityEl = document.getElementById('gacha-rarity');
        const nameEl = document.getElementById('gacha-name');

        if (!overlay || !card) return;

        // Add a "Skip" button if it doesn't exist
        let skipBtn = document.getElementById('gacha-skip-btn');
        if (queue.length > 1 && !skipBtn) {
            skipBtn = document.createElement('button');
            skipBtn.id = 'gacha-skip-btn';
            skipBtn.className = 'gacha-skip-btn';
            skipBtn.textContent = 'SALTAR TODO';
            overlay.appendChild(skipBtn);
        } else if (skipBtn && queue.length <= 1) {
            skipBtn.remove();
            skipBtn = null;
        }

        const showSummary = () => {
            if (skipBtn) skipBtn.remove();

            const summaryModal = document.createElement('div');
            summaryModal.className = 'modal-overlay gacha-summary-overlay';

            let itemsHTML = '';
            results.forEach(res => {
                const item = res.item;
                const mentorsDB = window.MENTORS_DB || [];
                const mentor = mentorsDB.find(m => m.id === item.id) || { name: item.name || item.id, rarity: item.rarity, icon: '🔮' };
                const isNewText = res.isNew 
                    ? `<span class="badge-new">NUEVO</span>` 
                    : `<span class="badge-old">+50 XP / +1 BADG</span>`;
                
                itemsHTML += `
                    <div class="card rarity-${mentor.rarity.toLowerCase()} gacha-summary-card">
                        <div class="gacha-summary-icon" style="color: var(--primary);"><i data-lucide="${mentor.icon || 'activity'}"></i></div>
                        <strong class="gacha-summary-name">${mentor.name}</strong>
                        <div class="gacha-summary-rarity">${mentor.rarity}</div>
                        ${isNewText}
                    </div>
                `;
            });

            summaryModal.innerHTML = `
              <div class="modal-content card gacha-summary-content">
                <h3>Resultados del Reclutamiento</h3>
                <div class="gacha-summary-subtitle">Staff contratado y registrado en el Clinical Hub.</div>
                
                <div class="gacha-results-grid">
                  ${itemsHTML}
                </div>
                
                <button id="close-pull-summary-btn" class="glass-btn gacha-summary-btn">Confirmar y Continuar</button>
              </div>
            `;

            document.body.appendChild(summaryModal);
            if (window.lucide) window.lucide.createIcons();
            document.getElementById('close-pull-summary-btn').onclick = () => {
                summaryModal.remove();
            };
        };

        const showNext = () => {
            if (queue.length === 0) {
                overlay.classList.remove('active');
                setTimeout(() => { 
                    overlay.style.display = 'none'; 
                    if (results.length > 1) {
                        showSummary();
                    }
                }, 500);
                return;
            }

            const res = queue.shift();
            const item = res.item;
            const mentorsDB = window.MENTORS_DB || [];
            const mentor = mentorsDB.find(m => m.id === item.id) || { name: item.name || item.id, rarity: item.rarity, icon: '🔮' };

            card.classList.remove('flipped');
            overlay.style.display = 'flex';
            void overlay.offsetWidth; // Force reflow
            overlay.classList.add('active');
            
            rarityEl.textContent = mentor.rarity;
            if (!res.isNew) {
                nameEl.innerHTML = `<span style="opacity:0.6; font-size:0.6em; display:block; margin-bottom:5px;">Duplicado</span>+50 XP / +1 BADG`;
                nameEl.style.color = "var(--accent)";
            } else {
                nameEl.textContent = mentor.name;
                nameEl.style.color = "inherit";
            }
            overlay.className = `gacha-reveal-overlay active rarity-${mentor.rarity.toLowerCase()}`;

            setTimeout(() => card.classList.add('flipped'), 100);
        };

        if (skipBtn) {
            skipBtn.onclick = (e) => {
                e.stopPropagation();
                overlay.classList.remove('active');
                overlay.style.display = 'none';
                showSummary();
            };
        }

        overlay.onclick = () => showNext();
        showNext();
    }

    _animateValue(id, end, duration = 800) {

        const el = this.ui[id] || document.getElementById(id);
        if (!el) return;
        const start = parseInt(el.textContent) || 0;
        if (start === end) { el.textContent = end; return; }
        this.activeAnimations.set(id, { el, start, end, duration, startTime: performance.now() });
        if (!this.animationFrameId) this.animationFrameId = requestAnimationFrame(this.animateAll);
    }

    animateAll(now) {
        let anyActive = false;
        for (const [id, anim] of this.activeAnimations) {
            const elapsed = now - anim.startTime;
            let progress = Math.min(elapsed / anim.duration, 1);
            let current = Math.floor(progress * (anim.end - anim.start) + anim.start);
            anim.el.textContent = current;
            if (progress < 1) anyActive = true;
            else { anim.el.textContent = anim.end; this.activeAnimations.delete(id); }
        }
        if (anyActive) this.animationFrameId = requestAnimationFrame(this.animateAll);
        else this.animationFrameId = null;
    }

    showShiftReport(rewards, stats) {
        const reportBody = document.getElementById('report-screen-body');
        if (!reportBody) return Promise.resolve();
        
        const isPerfect = stats.avgStability >= 100 && stats.correct === stats.total;
        const isGlow = rewards.grade === 'S' || rewards.grade === 'A';
        let narrativeText = `<div style="font-size: 1.1rem; line-height: 1.5; margin-bottom: 20px; font-family: var(--font-body); color: var(--ink);">`;
        if (stats.finalReport) {
            const r = stats.finalReport;
            narrativeText += `<strong>Guardia de ${r.specialty || 'General'} terminada.</strong> `;
            if (stats.majorBeat && stats.majorBeat.id === 'first_chain_survived') {
                 narrativeText += `Ese paciente de terapia intensiva está estable. Buen trabajo manejando el caso completo. `;
            } else if (r.totalEncounters > 1 && stats.lethals === 0) {
                 narrativeText += `El paciente salió de terapia estable. `;
            }
            if (stats.majorBeat && stats.majorBeat.id !== 'first_chain_survived') {
                 narrativeText += `<br><br><em>"${stats.majorBeat.text}" — ${stats.majorBeat.speaker}</em>`;
            }
            if (r.learningState && r.learningState.insights && r.learningState.insights.length > 0) {
                 // Get the pearl from the first insight
                 const worstInsight = r.learningState.insights[0];
                 narrativeText += `<div style="margin-top:15px; padding-left: 10px; border-left: 3px solid var(--accent); font-size: 0.9rem; opacity: 0.9;"><strong>Para la próxima:</strong> ${worstInsight.pearl}</div>`;
            }
        } else {
            narrativeText += `Guardia terminada.`;
        }
        narrativeText += `</div>`;

        reportBody.innerHTML = `
            <div class="${isGlow ? 'grade-glow' : ''}">
                <div style="font-size: 0.85rem; opacity: 0.6; margin-bottom: 20px; text-transform: uppercase; font-weight: bold; letter-spacing: 0.05em;">Notas de Guardia</div>
                
                ${narrativeText}
                
                ${rewards.unpaid ? `<div style="font-size: 0.8rem; margin-bottom: 15px; padding: 8px 12px; background: rgba(255,204,0,0.15); border-left: 4px solid var(--detail); text-align: left;">☕ <strong>Guardia de estudio</strong> (sin energía): XP reducido y sin créditos. Recupera energía para recompensas completas.</div>` : ''}

                <!-- Big Grade Badge -->
                <div style="display: inline-flex; align-items: center; justify-content: center; width: 100px; height: 100px; border-radius: 50%; background: var(--primary); color: white; font-size: 3rem; font-weight: 900; font-family: var(--font-heading); margin: 15px auto; box-shadow: 0 4px 15px rgba(0,0,0,0.3); border: 4px solid var(--accent);">
                ${rewards.grade}
                </div>
                
                <!-- Stats Grid -->
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 25px 0; text-align: left;">
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">Casos Clínicos</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--ink);">${stats.correct} / ${stats.total} <span style="font-size: 0.8rem; opacity: 0.7;">Correctos</span></div>
                </div>
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">Estabilidad Paciente</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--ink);">${Math.round(stats.avgStability)}%</div>
                </div>
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">XP Obtenida</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--primary);">+${rewards.xp} <span style="font-size: 0.8rem; opacity: 0.7;">XP</span></div>
                </div>
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">Moneda ENARM</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--accent);">+${rewards.credits} <span style="font-size: 0.8rem; opacity: 0.7;">$</span></div>
                </div>
                </div>
        
                <!-- Bonuses Section -->
                ${rewards.bonusPity > 0 || rewards.licensesWon > 0 || isPerfect ? `
                <div class="card" style="padding: 15px; background: linear-gradient(135deg, rgba(230,242,255,0.4), rgba(255,240,245,0.4)); text-align: left; margin-bottom: 25px; border-left: 4px solid var(--primary);">
                    <strong style="font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--primary);">Bonificaciones y Recompensas:</strong>
                    <ul style="margin: 8px 0 0 0; padding-left: 20px; font-size: 0.85rem; line-height: 1.5; color: var(--ink);">
                    ${rewards.bonusPity > 0 ? `<li>+${rewards.bonusPity} punto(s) de Pity en el Laboratorio (Garantizado SSR).</li>` : ''}
                    ${rewards.licensesWon > 0 ? `<li>+${rewards.licensesWon} Licencia(s) Médica(s) (para desbloquear salas avanzadas).</li>` : ''}
                    ${isPerfect ? `<li>☕ ¡Bono Guardia Perfecta! Mitigación de burnout y energía restablecida.</li>` : ''}
                    </ul>
                </div>
                ` : ''}
            </div>
        `;
        
        return new Promise((resolve) => {
            const backBtn = document.getElementById('report-back-btn');
            if (backBtn) {
                // Use a one-time click listener
                backBtn.onclick = () => {
                    resolve();
                };
            } else {
                resolve();
            }
        });
    }

    showFailureReport() {
        const reportBody = document.getElementById('report-screen-body');
        if (!reportBody) return Promise.resolve('exit');
        
        reportBody.innerHTML = `
          <div style="border-top: 6px solid var(--accent); padding: 20px;">
            <h2 style="font-family: var(--font-heading); color: var(--accent); margin-top: 0; font-size: 2rem; letter-spacing: 0.05em; text-transform: uppercase;">SIMULACIÓN FALLIDA</h2>
            
            <!-- Big Grade Badge -->
            <div style="display: inline-flex; align-items: center; justify-content: center; width: 100px; height: 100px; border-radius: 50%; background: var(--accent); color: white; font-size: 3rem; font-weight: 900; font-family: var(--font-heading); margin: 15px auto; box-shadow: 0 4px 15px rgba(225,29,72,0.3);">
              F
            </div>
            
            <p style="font-size: 1rem; opacity: 0.8; margin-bottom: 25px; line-height: 1.5;">Has cometido un error letal y la guardia ha sido cancelada.<br>Has perdido el progreso de este caso y tu energía.</p>
            
            <button id="retry-report-btn" class="glass-btn" style="width: 100%; font-weight: bold; padding: 12px; background: var(--primary); color: white; border: none; cursor: pointer; font-size: 1rem; border-radius: var(--radius-soft); margin-bottom: 10px;">Reintentar Caso</button>
            <div style="font-size: 0.8rem; opacity: 0.6; margin-top: 15px;">Usa el botón [X] arriba para salir al Hub Principal.</div>
          </div>
        `;
    
        return new Promise((resolve) => {
          const retryBtn = document.getElementById('retry-report-btn');
          const backBtn = document.getElementById('report-back-btn');

          if (retryBtn) {
            retryBtn.onclick = () => resolve('retry');
          }
          if (backBtn) {
            backBtn.onclick = () => resolve('exit');
          }
        });
    }

    renderEndOfDay(summary) {
        const body = document.getElementById('eod-body');
        if (!body) return;

        body.innerHTML = `
            <div style="font-size: 1.2rem; font-weight: bold; margin-bottom: 20px;">Has completado el Día ${summary.day}</div>
            
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; text-align: left; margin-bottom: 25px;">
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">Guardias Realizadas</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--ink);">${summary.guards}</div>
                </div>
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">Misiones Cumplidas</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--ink);">${summary.quests}</div>
                </div>
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">XP Generada</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--primary);">+${summary.totalXP} <span style="font-size: 0.8rem; opacity: 0.7;">XP</span></div>
                </div>
                <div class="card" style="padding: 15px; background: rgba(0,0,0,0.03);">
                    <div style="font-size: 0.75rem; opacity: 0.6; text-transform: uppercase; font-weight: bold;">Créditos Obtenidos</div>
                    <div style="font-size: 1.3rem; font-weight: bold; margin-top: 4px; color: var(--accent);">+${summary.totalCredits} <span style="font-size: 0.8rem; opacity: 0.7;">$</span></div>
                </div>
            </div>

            <div class="card" style="padding: 15px; background: linear-gradient(135deg, rgba(86, 216, 182, 0.2), rgba(255,255,255,0.1)); text-align: left; border-left: 4px solid var(--primary);">
                <strong style="font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--primary);">Bono de Cierre de Jornada:</strong>
                <p style="margin: 5px 0 0 0; font-size: 0.9rem;">+${summary.bonusXP} XP por tu dedicación.</p>
                <p style="margin: 5px 0 0 0; font-size: 0.8rem; opacity: 0.8;">El cansancio (burnout) ha disminuido por el descanso.</p>
            </div>
            
            ${summary.majorBeat ? `
            <div style="margin-top: 25px; padding: 20px; background: rgba(0, 0, 0, 0.05); border: 2px solid var(--ink); box-shadow: 4px 4px 0 var(--accent); border-radius: var(--radius-base);">
                <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px; color: var(--primary);">
                    <i data-lucide="stethoscope"></i>
                    <strong style="font-family: var(--font-heading); text-transform: uppercase; letter-spacing: 1px;">${summary.majorBeat.speaker}</strong>
                </div>
                <div style="font-size: 1.1rem; line-height: 1.5; color: var(--ink); font-style: italic;">
                    "${summary.majorBeat.text}"
                </div>
            </div>
            ` : ''}
        `;
        
        if (window.lucide) {
            window.lucide.createIcons();
        }
    }
}
